import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getUserDetails } from "../platform-components/utils/shared-data.service";
import { getData, apiPaths, getInstutueApp } from "../platform-components/api-integration-util";
import ChartCard from "../platform-components/ChartCard";
import { PreLoader } from "../platform-components/PreLoader";
import TeamMemberDetail from "./TeamMemberDetail";
import "./team-performance.css";

const DATE_PRESETS = [
  { label: "Today", days: 0 },
  { label: "Last 7 Days", days: 7 },
  { label: "Last 30 Days", days: 30 },
];

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getPresetDates(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

// Ensures end date covers the full day (23:59:59) for DATETIME column comparisons
function toApiDateRange(start, end) {
  return {
    startDate: start.length === 10 ? start + " 00:00:00" : start,
    endDate: end.length === 10 ? end + " 23:59:59" : end,
  };
}

const KPI_CONFIGS = [
  { key: "total_created", label: "Questions Created" },
  { key: "total_sent_for_review", label: "Sent for Review" },
  { key: "total_approved", label: "Approved" },
  { key: "total_rejected", label: "Rejected" },
  { key: "total_answer_conflicts_resolved", label: "Answer Conflicts Resolved" },
  { key: "total_classification_resolved", label: "Classification Reviewed" },
  { key: "total_variations_created", label: "Variations Created" },
  { key: "total_restyles_applied", label: "Restyles Applied" },
];

const ACTION_COLORS = {
  questionsCreated: "#1976d2",
  sentForReview: "#ff9800",
  approved: "#4caf50",
  rejected: "#f44336",
  answerConflictsResolved: "#9c27b0",
  classificationResolved: "#00bcd4",
  variationsCreated: "#795548",
  restylesApplied: "#607d8b",
};

export default function TeamPerformance() {
  const [loading, setLoading] = useState(true);
  const [activePreset, setActivePreset] = useState(0);
  const [customRange, setCustomRange] = useState(false);
  const [startDate, setStartDate] = useState(getPresetDates(0).startDate);
  const [endDate, setEndDate] = useState(getPresetDates(0).endDate);
  const [memberStats, setMemberStats] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  async function fetchData() {
    setLoading(true);
    try {
      const headers = {
        tenantId: getUserDetails().tenantId,
        userEmail: getUserDetails().email,
      };
      const params = toApiDateRange(startDate, endDate);

      // Fetch from both backends in parallel
      const [qbResponse, instituteResponse] = await Promise.all([
        getData(apiPaths.teamPerformanceOverview, { headers, params }),
        getInstutueApp(apiPaths.teamPerformanceInstOverview, params, headers),
      ]);

      const qbMembers = qbResponse?.result?.memberStats || [];
      const qbSummary = qbResponse?.result?.summary || {};
      const instMembers = instituteResponse?.result?.memberStats || [];
      const instSummary = instituteResponse?.result?.summary || {};

      // Merge members by email
      const merged = mergeMembers(qbMembers, instMembers);
      const mergedSummary = { ...qbSummary, ...instSummary };

      setMemberStats(merged);
      setSummary(mergedSummary);
    } catch (e) {
      console.error("Error fetching team performance", e);
      toast.error("Failed to load team performance data");
    }
    setLoading(false);
  }

  function mergeMembers(qbMembers, instMembers) {
    const map = {};

    qbMembers.forEach((m) => {
      const email = m.userEmail;
      map[email] = { ...map[email], ...m, userEmail: email };
    });

    instMembers.forEach((m) => {
      const email = m.userEmail;
      map[email] = { ...map[email], ...m, userEmail: email };
    });

    // Fill defaults and filter out members with all-zero data
    return Object.values(map)
      .map((m) => ({
        questionsCreated: 0,
        sentForReview: 0,
        approved: 0,
        rejected: 0,
        correctionRequested: 0,
        answerConflictsResolved: 0,
        answerConflictsDismissed: 0,
        classificationResolved: 0,
        classificationDismissed: 0,
        variationsCreated: 0,
        variationsApproved: 0,
        variationsRejected: 0,
        restylesApplied: 0,
        questionsGenerated: 0,
        ...m,
      }))
      .filter((m) => {
        const total =
          Number(m.questionsCreated || 0) +
          Number(m.sentForReview || 0) +
          Number(m.approved || 0) +
          Number(m.rejected || 0) +
          Number(m.correctionRequested || 0) +
          Number(m.answerConflictsResolved || 0) +
          Number(m.answerConflictsDismissed || 0) +
          Number(m.classificationResolved || 0) +
          Number(m.classificationDismissed || 0) +
          Number(m.variationsCreated || 0) +
          Number(m.variationsApproved || 0) +
          Number(m.variationsRejected || 0) +
          Number(m.restylesApplied || 0) +
          Number(m.questionsGenerated || 0);
        return total > 0;
      });
  }

  function handlePreset(days) {
    setCustomRange(false);
    setActivePreset(days);
    const { startDate: s, endDate: e } = getPresetDates(days);
    setStartDate(s);
    setEndDate(e);
  }

  function handleCustomRange() {
    setCustomRange(true);
    setActivePreset(null);
  }

  function getTotalActions(member) {
    return (
      Number(member.questionsCreated || 0) +
      Number(member.sentForReview || 0) +
      Number(member.approved || 0) +
      Number(member.rejected || 0) +
      Number(member.answerConflictsResolved || 0) +
      Number(member.classificationResolved || 0) +
      Number(member.variationsCreated || 0) +
      Number(member.restylesApplied || 0)
    );
  }

  function exportCSV() {
    if (!memberStats.length) return;
    const headers = [
      "Email",
      "Questions Created",
      "Sent for Review",
      "Approved",
      "Rejected",
      "Correction Requested",
      "Answer Conflicts Resolved",
      "Classification Reviewed",
      "Variations Created",
      "Variations Approved",
      "Restyles Applied",
      "Questions Generated",
      "Total Actions",
    ];
    const rows = memberStats.map((m) =>
      [
        m.userEmail,
        m.questionsCreated,
        m.sentForReview,
        m.approved,
        m.rejected,
        m.correctionRequested,
        m.answerConflictsResolved,
        m.classificationResolved,
        m.variationsCreated,
        m.variationsApproved,
        m.restylesApplied,
        m.questionsGenerated,
        getTotalActions(m),
      ].join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `team-performance-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function buildMemberChartData() {
    return [...memberStats]
      .sort((a, b) => getTotalActions(b) - getTotalActions(a))
      .map((m) => ({
        name: m.userEmail.split("@")[0],
        count: getTotalActions(m),
      }))
      .filter((d) => d.count > 0);
  }

  function buildActionTypeChartData() {
    const actionTotals = [
      { key: "questionsCreated", name: "Created" },
      { key: "sentForReview", name: "Sent for Review" },
      { key: "approved", name: "Approved" },
      { key: "rejected", name: "Rejected" },
      { key: "answerConflictsResolved", name: "Answer Conflicts" },
      { key: "classificationResolved", name: "Classification" },
      { key: "variationsCreated", name: "Variations" },
      { key: "restylesApplied", name: "Restyles" },
    ];
    return actionTotals
      .map((t) => ({
        name: t.name,
        count: memberStats.reduce((sum, m) => sum + Number(m[t.key] || 0), 0),
      }))
      .filter((d) => d.count > 0);
  }

  // Drilldown into a member
  if (selectedMember) {
    return (
      <TeamMemberDetail
        memberEmail={selectedMember}
        startDate={startDate}
        endDate={endDate}
        onBack={() => setSelectedMember(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="team-perf-loading">
        <PreLoader />
      </div>
    );
  }

  const sortedMembers = [...memberStats].sort(
    (a, b) => getTotalActions(b) - getTotalActions(a)
  );

  return (
    <div className="team-performance-container">
      {/* Header with filters */}
      <div className="team-perf-header">
        <h2>Team Performance</h2>
        <div className="team-perf-filters">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.days}
              className={`team-perf-date-btn ${
                activePreset === p.days && !customRange ? "active" : ""
              }`}
              onClick={() => handlePreset(p.days)}
            >
              {p.label}
            </button>
          ))}
          <button
            className={`team-perf-date-btn ${customRange ? "active" : ""}`}
            onClick={handleCustomRange}
          >
            Custom
          </button>
          {customRange && (
            <div className="team-perf-custom-dates">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
          <button className="team-perf-export-btn" onClick={exportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="kpi-cards-row">
        {KPI_CONFIGS.map((kpi) => (
          <div className="kpi-card" key={kpi.key}>
            <div className="kpi-value">{Number(summary[kpi.key] || 0)}</div>
            <div className="kpi-label">{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Team Member Cards */}
      {sortedMembers.length === 0 ? (
        <div className="team-perf-no-data">
          No team activity found for the selected period.
        </div>
      ) : (
        <>
          <div className="team-members-section">
            <h3>Team Members ({sortedMembers.length})</h3>
            <div className="team-member-cards-grid">
              {sortedMembers.map((member) => {
                const total = getTotalActions(member);
                return (
                  <div
                    key={member.userEmail}
                    className="team-member-card"
                    onClick={() => setSelectedMember(member.userEmail)}
                  >
                    <div className="team-member-card-header">
                      <span className="member-name">{member.userEmail}</span>
                      <span className="total-actions">{total} actions</span>
                    </div>
                    <div className="team-member-card-body">
                      <div className="member-stats-grid">
                        <div className="member-stat-item">
                          <div className="stat-value">
                            {Number(member.questionsCreated || 0)}
                          </div>
                          <div className="stat-label">Created</div>
                        </div>
                        <div className="member-stat-item">
                          <div className="stat-value">
                            {Number(member.approved || 0)}
                          </div>
                          <div className="stat-label">Approved</div>
                        </div>
                        <div className="member-stat-item">
                          <div className="stat-value">
                            {Number(member.rejected || 0)}
                          </div>
                          <div className="stat-label">Rejected</div>
                        </div>
                        <div className="member-stat-item">
                          <div className="stat-value">
                            {Number(member.answerConflictsResolved || 0)}
                          </div>
                          <div className="stat-label">Ans. Conflicts</div>
                        </div>
                        <div className="member-stat-item">
                          <div className="stat-value">
                            {Number(member.classificationResolved || 0)}
                          </div>
                          <div className="stat-label">Classification</div>
                        </div>
                        <div className="member-stat-item">
                          <div className="stat-value">
                            {Number(member.variationsCreated || 0) +
                              Number(member.restylesApplied || 0)}
                          </div>
                          <div className="stat-label">Variations/Restyles</div>
                        </div>
                      </div>
                      {total > 0 && (
                        <div className="member-action-bar">
                          {Object.entries(ACTION_COLORS).map(([key, color]) => {
                            const val = Number(member[key] || 0);
                            if (val === 0) return null;
                            return (
                              <div
                                key={key}
                                className="bar-segment"
                                style={{
                                  width: `${(val / total) * 100}%`,
                                  backgroundColor: color,
                                }}
                                title={`${key}: ${val}`}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Charts */}
          <div className="team-perf-charts">
            <ChartCard
              title="Actions by Team Member"
              data={buildMemberChartData()}
              chartType="bar"
              height={400}
            />
            <ChartCard
              title="Action Type Distribution"
              data={buildActionTypeChartData()}
              chartType="donut"
              height={400}
            />
          </div>
        </>
      )}
    </div>
  );
}
